/**
 * Stance display: a small window-less HUD portrait in a bottom corner showing
 * the player's own model — the two-tile `@` + legs (`ハ`/`メ`), collapsed to one
 * tile when crouching — so you can always read your stance. When focus mode is
 * active the character "holds up" the card suit of its active weapon (the suit
 * that represents that slot: ♣ ♦ ♠ ♥) above its head.
 *
 * Canvas + fillText, no GameWindow chrome (fixed to the viewport corner).
 */

import type { Player } from '../sim/player';

const CELL = 22;
const W = 92;
const H = 118;
/** Card suits per weapon slot — matches the taskbar (♣ Main · ♦ Side · ♠ Melee · ♥ Heal). */
const SUITS = ['♣', '♦', '♠', '♥'] as const;
const SUIT_COLOR = ['#d6e2f0', '#d6e2f0', '#d6e2f0', '#ff5b6e'] as const; // ♣♦♠ pale, ♥ heal red

export class StanceDisplay {
  private readonly ctx: CanvasRenderingContext2D;
  private legPhase = 0;

  constructor(parent: HTMLElement) {
    const el = document.createElement('canvas');
    el.width = W;
    el.height = H;
    el.className = 'stance-display';
    el.style.position = 'fixed';
    el.style.left = '14px';
    el.style.bottom = '128px'; // clear of the 118px taskbar
    el.style.zIndex = '31';
    el.style.pointerEvents = 'none';
    el.style.background = 'rgba(8, 14, 22, 0.62)';
    el.style.border = '1px solid rgba(120, 200, 255, 0.35)';
    el.style.borderRadius = '3px';
    el.style.boxShadow = '0 0 10px rgba(80,180,255,0.14), 0 3px 14px rgba(0,0,0,0.5)';
    parent.appendChild(el);
    const ctx = el.getContext('2d');
    if (!ctx) throw new Error('stanceDisplay: no 2d context');
    this.ctx = ctx;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
  }

  update(player: Player, dtSec: number): void {
    const c = this.ctx;
    c.clearRect(0, 0, W, H);
    const cx = W / 2;
    const legsY = H - 34; // leave room for the label

    // Dead → lie the model on the floor.
    if (player.body.dead) {
      c.font = `${CELL - 2}px "Cascadia Mono","Consolas","DejaVu Sans Mono",monospace`;
      c.fillStyle = '#9a5a5a';
      const floorY = H - 26;
      c.save();
      c.translate(cx, floorY);
      c.rotate(Math.PI / 2); // toppled onto its side
      c.fillText('@', 0, -CELL * 0.55);
      c.fillText('ハ', 0, CELL * 0.55);
      c.restore();
      c.font = '9px "Cascadia Mono",monospace';
      c.fillStyle = '#ff6a6a';
      c.fillText('DOWN', cx, H - 10);
      return;
    }

    const crouching = player.stance === 'crouch';
    const moving = player.moveIntentDx !== 0 || player.moveIntentDy !== 0;
    if (moving) this.legPhase += dtSec * 6;
    const torsoY = crouching ? legsY : legsY - CELL;

    // Cover: draw the wall the player is hugging beside the character. Cover
    // ABOVE the player is shown on the RIGHT; cover BELOW on the LEFT; cover to
    // a side is shown on that side. (Drawing it directly above/below would
    // collide with the held suit / floor line, so vertical cover is turned to
    // the side.)
    if (player.inCover) {
      const midY = (torsoY + legsY) / 2;
      let wallX: number;
      if (player.coverDy < 0) wallX = cx + CELL; // cover above → right
      else if (player.coverDy > 0) wallX = cx - CELL; // cover below → left
      else wallX = cx + player.coverDx * CELL; // side cover → that side
      c.font = `${CELL}px "Cascadia Mono","Consolas","DejaVu Sans Mono",monospace`;
      c.fillStyle = '#8a95a4';
      c.fillText('█', wallX, midY);
    }

    // Legs (ハ / メ walk frames), cyan like the in-game player.
    c.font = `${CELL - 2}px "Cascadia Mono","Consolas","DejaVu Sans Mono",monospace`;
    c.fillStyle = '#22e6ff';
    const legGlyph = moving && Math.sin(this.legPhase) < 0 ? 'メ' : 'ハ';
    c.fillText(legGlyph, cx, legsY);
    // Torso.
    c.fillText('@', cx, torsoY);

    // Focus: hold the active weapon's card suit out to the RIGHT of the torso.
    if (player.focusActive) {
      const idx = player.activeWeaponIdx;
      const suitX = cx + CELL * 0.95;
      c.font = `${CELL}px "Segoe UI Symbol","DejaVu Sans",serif`;
      c.fillStyle = SUIT_COLOR[idx];
      c.shadowColor = '#ffcf50';
      c.shadowBlur = 8;
      c.fillText(SUITS[idx], suitX, torsoY);
      c.shadowBlur = 0;
    }

    // Labels. Report the actual locomotion state: RUN / WALK (moving) vs STAND
    // (idle) vs CROUCH, with IN COVER taking precedence.
    let stanceLabel: string;
    if (player.inCover) stanceLabel = 'IN COVER';
    else if (crouching) stanceLabel = 'CROUCH';
    else if (player.stance === 'run') stanceLabel = 'RUN';
    else stanceLabel = moving ? 'WALK' : 'STAND';
    c.font = '9px "Cascadia Mono",monospace';
    c.fillStyle = player.inCover ? '#7fe0a0'
      : player.stance === 'run' ? '#e0a040'
      : player.focusActive ? '#ffe060'
      : '#8fb0c8';
    c.fillText(stanceLabel, cx, H - 10);
    if (player.focusActive) {
      c.fillStyle = '#ffe060';
      c.fillText('◆ FOCUS ◆', cx, H - 22);
    }
  }
}
